<template>
  <div class="login-page">
    <!-- ========== LEFT PANEL: Branding ========== -->
    <div class="login-brand-panel">
      <!-- Background decorations -->
      <div class="brand-bg-shapes">
        <div class="brand-shape shape-1"></div>
        <div class="brand-shape shape-2"></div>
        <div class="brand-shape shape-3"></div>
      </div>

      <div class="brand-content">
        <!-- Logo -->
        <div class="brand-logo">
          <img src="@/assets/logo.png" alt="Logo" class="brand-logo-img" />
        </div>

        <!-- App name -->
        <p class="brand-app-name">{{ appTitle }}</p>

        <!-- Brand text -->
        <h1 class="brand-title">Empowering Abilities,<br />Transforming Lives.</h1>
      </div>
    </div>

    <!-- ========== RIGHT PANEL: Form ========== -->
    <div class="login-form-panel">
      <div class="form-wrapper">
        <!-- Mobile-only logo -->
        <div class="mobile-logo">
          <img src="@/assets/logo.png" alt="Logo" class="mobile-logo-img" />
          <h2 class="mobile-logo-title">{{ appTitle }}</h2>
        </div>

        <!-- Header -->
        <div class="form-header">
          <h2 class="form-title">Welcome back</h2>
          <p class="form-description">Sign in to your account to continue</p>
        </div>

        <!-- Form -->
        <form class="form-body" @submit.prevent="handleLoginSubmit">
          <BaseInput
            v-model="email"
            label="Email address"
            type="email"
            placeholder="you@example.com"
            autocomplete="email"
            required
          />

          <BaseInput
            v-model="password"
            label="Password"
            type="password"
            placeholder="••••••••"
            autocomplete="current-password"
            required
          />

          <!-- Row: Remember me & Forgot password -->
          <div class="form-row">
            <label class="remember-me">
              <input
                type="checkbox"
                v-model="rememberMe"
                class="remember-checkbox"
              />
              <span class="remember-text">Remember me</span>
            </label>

            <button
              type="button"
              class="forgot-link"
              @click="showForgotModal = true"
            >
              Forgot password?
            </button>
          </div>

          <!-- Submit -->
          <BaseButton
            type="submit"
            variant="primary"
            size="lg"
            :loading="loading"
            fullWidth
          >
            {{ loading ? 'Signing in...' : 'Sign in' }}
          </BaseButton>
        </form>

        <!-- Divider 
        <div class="form-divider">
          <span class="divider-line"></span>
          <span class="divider-text">or continue with</span>
          <span class="divider-line"></span>
        </div>

        Social login buttons (placeholder for future)
        <div class="social-buttons">
          <button type="button" class="social-btn">
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Google
          </button>
        </div>-->


        <!-- Sign up link -->
        <p class="form-footer mt-8">
          Don't have an account?
          <router-link to="/register" class="form-footer-link">
            Sign up
          </router-link>
        </p>
      </div>
    </div>

    <!-- ========== MODALS ========== -->
    <!-- Forgot Password Modal -->
    <BaseModal
      v-model="showForgotModal"
      title="Reset your password"
      size="sm"
    >
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Enter your email address and we'll send you a link to reset your password.
      </p>

      <BaseInput
        v-model="resetEmail"
        label="Email address"
        type="email"
        placeholder="you@example.com"
        autocomplete="email"
      />

      <template #footer>
        <BaseButton variant="ghost" @click="closeForgotModal">
          Cancel
        </BaseButton>
        <BaseButton
          variant="primary"
          :loading="forgotLoading"
          @click="handleResetPassword"
        >
          {{ forgotLoading ? 'Sending...' : 'Send reset link' }}
        </BaseButton>
      </template>
    </BaseModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useTitle } from '@/composables/ui/useTitle'
import { useLogin } from '@/composables/auth/useLogin'
import { useForgotPassword } from '@/composables/auth/useForgotPassword'
import { BaseInput, BaseButton, BaseModal } from '@/components/ui'

const appTitle = computed(() => process.env.VUE_APP_TITLE || '')

useTitle(`${appTitle.value} - Login`)

const { email, password, loading, handleLogin } = useLogin()
const rememberMe = ref(false)

// Forgot password modal state
const showForgotModal = ref(false)
const {
  email: resetEmail,
  loading: forgotLoading,
  handleForgotPassword,
  reset: resetForgotForm
} = useForgotPassword()

function handleLoginSubmit() {
  // Clear any previous error before attempting login
  handleLogin()
}

function closeForgotModal() {
  showForgotModal.value = false
  resetForgotForm()
}

async function handleResetPassword() {
  const sent = await handleForgotPassword()
  if (sent) {
    showForgotModal.value = false
    resetForgotForm()
  }
}
</script>

<style scoped>
/* ============================================
   LOGIN PAGE - SPLIT LAYOUT
   ============================================ */

.login-page {
  @apply min-h-screen flex flex-col lg:flex-row;
}

/* ============================================
   LEFT: BRAND PANEL
   ============================================ */

.login-brand-panel {
  @apply relative hidden lg:flex lg:w-5/12 xl:w-5/12
         bg-gradient-to-br from-primary via-primary-hover to-primary-active
         dark:from-gray-900 dark:via-primary-dark-bg dark:to-gray-900
         overflow-hidden;
}

.brand-bg-shapes {
  @apply absolute inset-0 overflow-hidden pointer-events-none;
}

.brand-shape {
  @apply absolute rounded-full opacity-10;
}

.shape-1 {
  @apply w-96 h-96 -top-20 -right-20
         bg-white dark:bg-primary-dark;
  animation: float 20s ease-in-out infinite;
}

.shape-2 {
  @apply w-64 h-64 bottom-10 -left-10
         bg-white dark:bg-primary-dark;
  animation: float 25s ease-in-out infinite reverse;
}

.shape-3 {
  @apply w-48 h-48 top-1/2 right-10
         bg-white/50 dark:bg-primary-dark/50;
  animation: float 30s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

.brand-content {
  @apply relative z-10 flex flex-col justify-center
         px-12 xl:px-20
         text-white;
}

.brand-logo {
  @apply mb-8;
}

.brand-logo-img {
  @apply h-12 w-12 rounded-2xl object-contain;
}

.brand-app-name {
  @apply text-lg font-bold uppercase tracking-widest
         text-white/90 mb-4;
}

.brand-title {
  @apply text-3xl xl:text-4xl font-bold tracking-tight mb-4;
}

.brand-subtitle {
  @apply text-base xl:text-lg text-white/80 leading-relaxed max-w-md;
}

.brand-features {
  @apply mt-10 space-y-4;
}

.brand-feature {
  @apply flex items-center gap-3 text-white/90 text-sm;
}

.feature-icon {
  @apply flex-shrink-0 h-5 w-5
         bg-white/20 rounded-full p-0.5;
}

/* ============================================
   RIGHT: FORM PANEL
   ============================================ */

.login-form-panel {
  @apply flex-1 flex items-center justify-center
         bg-gray-50 dark:bg-gray-900
         px-4 py-8 sm:px-6 sm:py-8 lg:p-8
         overflow-y-auto;
}

.form-wrapper {
  @apply w-full max-w-md mx-auto;
}

.mobile-logo {
  @apply flex flex-col items-center gap-3 mb-6 lg:hidden
         rounded-2xl py-6
         bg-gradient-to-br from-primary/5 via-primary/10 to-primary-hover/5
         dark:from-primary-dark/10 dark:via-primary-dark/5 dark:to-primary-dark/10;
}

.mobile-logo-img {
  @apply h-14 w-14 rounded-2xl object-contain;
}

.mobile-logo-title {
  @apply text-base font-bold text-primary dark:text-primary-dark uppercase tracking-wider;
}

.form-header {
  @apply mb-6;
}

.form-title {
  @apply text-2xl sm:text-3xl font-bold
         text-gray-900 dark:text-white;
}

.form-description {
  @apply mt-2 text-sm text-gray-600 dark:text-gray-400;
}

.form-body {
  @apply space-y-5;
}

/* Row: Remember me + Forgot */
.form-row {
  @apply flex items-center justify-between;
}

.remember-me {
  @apply flex items-center gap-2 cursor-pointer select-none;
}

.remember-checkbox {
  @apply h-4 w-4 rounded
         border-gray-300 dark:border-gray-600
         text-primary focus:ring-primary/40
         dark:bg-gray-700 dark:focus:ring-primary-dark/40
         cursor-pointer;
}

.remember-text {
  @apply text-sm text-gray-600 dark:text-gray-400;
}

.forgot-link {
  @apply text-sm font-medium
         text-primary hover:text-primary-hover
         dark:text-primary-dark dark:hover:text-primary-dark-hover
         transition-colors duration-150;
}

/* Divider 
.form-divider {
  @apply relative my-6 flex items-center;
}

.divider-line {
  @apply flex-1 border-t border-gray-300 dark:border-gray-600;
}

.divider-text {
  @apply px-4 text-xs text-gray-500 dark:text-gray-400 font-medium;
}*/

/* Social buttons 
.social-buttons {
  @apply flex justify-center;
}

.social-btn {
  @apply inline-flex items-center gap-2.5
         px-5 py-2.5
         border border-gray-300 dark:border-gray-600
         rounded-xl
         text-sm font-medium text-gray-700 dark:text-gray-300
         hover:bg-gray-100 dark:hover:bg-gray-800
         transition-colors duration-150;
}*/

/* Form footer */
.form-footer {
  @apply text-center text-sm
         text-gray-600 dark:text-gray-400;
}

.form-footer-link {
  @apply font-semibold text-primary dark:text-primary-dark
         hover:text-primary-hover dark:hover:text-primary-dark-hover
         transition-colors duration-200;
}

/* ============================================
   RESPONSIVE
   ============================================ */

@media (max-width: 1024px) {
  .login-brand-panel {
    @apply hidden;
  }

  .login-form-panel {
    @apply min-h-screen;
  }
}

@media (min-width: 1024px) {
  .login-form-panel {
    @apply min-h-screen;
  }
}
</style>
 